// Hideo - script comments, without UI. The commented text is wrapped in <span class="cmt" data-cmt="<id>"> inside the
// tab's HTML; the comment lives in script.comments: { id, tabId, quote, text, author, at, edited, resolved, resolvedAt,
// replies: [{ text, author, at }] }. The column, the buttons and the keys are the comments store (src/stores/comments.ts)
// and CommentsPanel.vue. Ported with
// the same results (golden tests: tests/editor-comments.test.ts against tests/fixtures/legacy/comments.js).
// Known limits kept for now (docs/backlog.md): duplicating, copying or pasting blocks duplicates the spans with the same
// id (cmtWrap does not check).
import { LOCALE, t } from '../../i18n'
import { useDataStore } from '../../stores/data'
import type { Script, ScriptComment } from '../../data/types'
import { edBodyEl, noZw } from './html'

/** A new comment id: "cm_" + time + 4 random characters */
export const cmtNewId = (): string => 'cm_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
/** Author of a new comment or reply: the profile's name, else "You" */
export const cmtAuthor = (): string => { const p = useDataStore().currentProfile(); return (p && p.name) || t('You') }
/** Date and time of a comment, short ("17 ott, 10:30") */
export const cmtWhen = (ts: number): string => new Date(ts).toLocaleString(LOCALE, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/** Highlights of a comment in the page */
export const cmtSpans = (id: string, root: ParentNode = edBodyEl()): HTMLElement[] =>
  [...root.querySelectorAll<HTMLElement>('.cmt')].filter(s => s.dataset.cmt === id)
/** Removes a comment's highlights (the text stays) */
export function cmtUnwrap(id: string, root: ParentNode = edBodyEl()): void { cmtSpans(id, root).forEach(sp => sp.replaceWith(...sp.childNodes)) }

/** Wraps the text of a range (even across paragraphs) in one span.cmt per text node; skips the text directly inside
 *  lists, blank text directly inside the page and mention chips. Returns the wrapped pieces of text. */
export function cmtWrap(r: Range, id: string, body: Node = edBodyEl()): string[] {
  const anc = r.commonAncestorContainer, root = anc.nodeType === 3 ? anc.parentNode as Node : anc, parts: [Text, number, number][] = []
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  for (let n = w.nextNode() as Text | null; n; n = w.nextNode() as Text | null) {
    if (!r.intersectsNode(n) || !(n.textContent as string).length) continue
    const p = n.parentElement
    if (!p || /^(UL|OL)$/.test(p.tagName) || p === body && !(n.textContent as string).trim() || p.closest('.mention-chip')) continue
    const s = n === r.startContainer ? r.startOffset : 0, e = n === r.endContainer ? r.endOffset : n.length
    if (s < e) parts.push([n, s, e])
  }
  const texts = parts.map(([n, s, e]) => (n.textContent as string).slice(s, e))   // pieces from different paragraphs: joined with a space in the quote
  parts.forEach(([n, s, e]) => {
    let node = n
    if (e < node.length) node.splitText(e)
    if (s > 0) node = node.splitText(s)
    const sp = document.createElement('span'); sp.className = 'cmt'; sp.dataset.cmt = id; node.replaceWith(sp); sp.appendChild(node)
  })
  return texts
}
/** The quote shown on the comment's card: the wrapped pieces joined, one space between words, at most 160 characters */
export const cmtQuote = (texts: string[]): string => noZw(texts.join(' ')).replace(/\s+/g, ' ').trim().slice(0, 160)

/** Comments of the script's open tab (none without a script) */
export const tabComments = (sc: Script | null | undefined): ScriptComment[] => sc ? (sc.comments || []).filter(c => c.tabId === sc.activeTab) : []
/** Comment ids in the order their highlights appear in the page (each once) */
export const cmtOrder = (root: ParentNode): string[] => [...new Set([...root.querySelectorAll<HTMLElement>('.cmt')].map(s => s.dataset.cmt as string))]
/** The column's order: open comments as their text appears (those whose text was deleted last, oldest first), resolved
 *  ones last resolved first */
export function cmtSort(all: ScriptComment[], order: string[]): { open: ScriptComment[]; done: ScriptComment[] } {
  const pos = (c: ScriptComment) => { const i = order.indexOf(c.id); return i < 0 ? 1e9 + c.at : i }
  return {
    open: all.filter(c => !c.resolved).sort((a, b) => pos(a) - pos(b)),
    done: all.filter(c => c.resolved).sort((a, b) => (b.resolvedAt as number) - (a.resolvedAt as number))
  }
}
/** Removes the highlights without an open comment (e.g. a comment started and never saved before closing); draftId =
 *  the comment being written. Returns how many spans were removed. */
export function cmtCleanupIn(root: ParentNode, comments: ScriptComment[] | undefined, draftId?: string | null): number {
  const ids = new Set((comments || []).filter(c => !c.resolved).map(c => c.id))
  if (draftId) ids.add(draftId)
  let n = 0
  root.querySelectorAll<HTMLElement>('.cmt').forEach(sp => { if (!ids.has(sp.dataset.cmt as string)) { sp.replaceWith(...sp.childNodes); n++ } })
  return n
}

// ----- The comment model (the caller saves the data) -----
/** A new comment on the open tab */
export function cmtAdd(sc: Script, draft: { id: string; quote: string }, text: string, author: string, at: number): void {
  (sc.comments = sc.comments || []).push({ id: draft.id, tabId: sc.activeTab as string, quote: draft.quote, text, author, at, resolved: false, replies: [] })
}
/** Resolved: archived (the caller removes its highlights) */
export const cmtResolved = (c: ScriptComment, at: number): void => { c.resolved = true; c.resolvedAt = at }
/** Deletes a comment and its replies */
export const cmtRemove = (sc: Script, id: string): void => { sc.comments = (sc.comments as ScriptComment[]).filter(x => x.id !== id) }
/** New text of a comment (trimmed); false when it did not change (nothing to save) */
export const cmtEdited = (c: ScriptComment, txt: string, at: number): false | void => {
  if (c.text === txt.trim()) return false
  c.text = txt.trim(); c.edited = at
}
/** A reply under the comment (text trimmed) */
export const cmtReplied = (c: ScriptComment, txt: string, author: string, at: number): void => {
  (c.replies = c.replies || []).push({ text: txt.trim(), author, at })
}
